<script setup>
import { ref, computed } from 'vue'
import AddSubjectModal from '@/Components/Modals/AddSubjectModal.vue'

const props = defineProps({
  subjects: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Teaching Subjects'
  },
  description: {
    type: String,
    default: 'Select all subjects you\'re qualified and comfortable teaching'
  },
  showShadow: {
    type: Boolean,
    default: true
  },
  viewMode: {
    type: String,
    default: 'detailed', // 'detailed' or 'simple'
    validator: (value) => ['detailed', 'simple'].includes(value)
  }
})

const emit = defineEmits(['update:modelValue', 'subject-added'])

const localErrors = ref({})
const showAddSubjectModal = ref(false)
const localSubjects = ref([...props.subjects])
const searchQuery = ref('')

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const filteredSubjects = computed(() => {
  if (!searchQuery.value.trim()) {
    return localSubjects.value
  }

  const query = searchQuery.value.toLowerCase().trim()
  return localSubjects.value.filter(subject =>
    subject.name.toLowerCase().includes(query) ||
    (subject.description && subject.description.toLowerCase().includes(query))
  )
})

const toggleSubject = (id) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(id)

  if (index === -1) {
    selected.push(id)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.selected_subjects) {
    delete localErrors.value.selected_subjects
  }
}

const isSelected = (id) => props.modelValue.includes(id)

const validate = () => {
  const errors = {}

  if (props.modelValue.length === 0) {
    errors.selected_subjects = 'Please select at least one subject'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

const handleSubjectAdded = (newSubject) => {
  // Add the new subject to the local list
  localSubjects.value.push(newSubject)

  // Auto-select the newly added subject
  const selected = [...props.modelValue, newSubject.id]
  emit('update:modelValue', selected)
  emit('subject-added', newSubject)

  // Close modal
  showAddSubjectModal.value = false
}

defineExpose({ validate })
</script>

<template>
  <div :class="['bg-white rounded-2xl p-4 md:p-10', showShadow ? 'shadow-lg' : '']">
    <div class="mb-4 md:mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600 text-sm">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.selected_subjects" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.selected_subjects }}</p>
            <p class="text-red-700 text-sm mt-1">Choose at least one subject from the list below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Filter -->
    <div class="mb-6">
      <div class="relative">
        <div class="absolute left-4 top-1/2 transform -translate-y-1/2">
          <span class="material-symbols-outlined text-gray-400 text-2xl">search</span>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search subjects by name or description..."
          class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 text-base"
        />
        <button
          v-if="searchQuery"
          type="button"
          @click="searchQuery = ''"
          class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>
      <p v-if="searchQuery && filteredSubjects.length === 0" class="text-sm text-gray-500 mt-2 flex items-center">
        <span class="material-symbols-outlined text-sm mr-1">info</span>
        No subjects found matching "{{ searchQuery }}". Try adding it using the button below.
      </p>
      <p v-else-if="searchQuery" class="text-sm text-gray-500 mt-2">
        Showing {{ filteredSubjects.length }} of {{ localSubjects.length }} subjects
      </p>
    </div>

    <!-- Detailed View -->
    <div v-if="viewMode === 'detailed'" class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <button
        v-for="subject in filteredSubjects"
        :key="subject.id"
        type="button"
        @click="toggleSubject(subject.id)"
        class="group flex flex-col items-center justify-between p-4 rounded-xl border-2 transition-all h-48 hover:shadow-md"
        :class="isSelected(subject.id) ? 'shadow-md' : 'border-gray-200 bg-white'"
        :style="{
          borderColor: isSelected(subject.id) ? subject.color : '',
          backgroundColor: isSelected(subject.id) ? `${subject.color}10` : ''
        }"
      >
        <div class="flex flex-col items-center w-full overflow-hidden">
          <div
            class="flex items-center justify-center mb-2 p-4 rounded-2xl"
            :style="{ backgroundColor: `${subject.color}10` }"
          >
            <span
              class="material-symbols-outlined text-4xl"
              :style="{ color: subject.color }"
            >
              {{ subject.icon_name }}
            </span>
          </div>
          <span class="text-md font-bold text-gray-900 text-center">{{ subject.name }}</span>
          <span class="text-sm text-gray-500 text-center w-full overflow-hidden text-ellipsis whitespace-nowrap px-1">{{ subject.description || '' }}</span>
        </div>

        <div
          v-if="isSelected(subject.id)"
          class="mt-2"
          :style="{ color: subject.color }"
        >
          <span class="material-symbols-outlined text-xl">check_circle</span>
        </div>
      </button>
    </div>

    <!-- Simple View -->
    <div v-else class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <button
        v-for="subject in filteredSubjects"
        :key="subject.id"
        type="button"
        @click="toggleSubject(subject.id)"
        class="relative group px-4 py-3 rounded-lg border-2 transition-all hover:shadow-sm"
        :class="isSelected(subject.id) ? 'border-opacity-100' : 'border-gray-200 bg-white hover:border-gray-300'"
        :style="{
          borderColor: isSelected(subject.id) ? subject.color : '',
          backgroundColor: isSelected(subject.id) ? `${subject.color}08` : ''
        }"
      >
        <span class="text-sm font-semibold text-gray-900 text-center block">{{ subject.name }}</span>

        <!-- Triangle indicator for selected subjects -->
        <div
          v-if="isSelected(subject.id)"
          class="absolute top-0 right-0 w-0 h-0"
          :style="{
            borderTop: `20px solid ${subject.color}`,
            borderLeft: '20px solid transparent'
          }"
        ></div>
      </button>
    </div>

    <!-- Add Subject Button -->
      <div class="mt-8">
        <button
          type="button"
          @click="showAddSubjectModal = true"
          class="group w-full flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-gray-300 transition-all h-36 hover:border-brand-blue hover:bg-blue-50"
        >
          <div class="flex flex-col items-center">
            <div class="flex items-center justify-center mb-2 p-4 rounded-2xl bg-gray-50 group-hover:bg-blue-100 transition-colors">
              <span class="material-symbols-outlined text-4xl text-gray-400 group-hover:text-brand-blue transition-colors">
                add_circle
              </span>
            </div>
            <span class="text-md font-bold text-gray-600 group-hover:text-brand-blue text-center transition-colors">Add Subject</span>
            <span class="text-sm text-gray-500 text-center mt-1">Can't find yours?</span>
          </div>
        </button>
      </div>

    <!-- Add Subject Modal -->
    <AddSubjectModal
      :show="showAddSubjectModal"
      @close="showAddSubjectModal = false"
      @subject-added="handleSubjectAdded"
    />
  </div>
</template>
